import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  ArrowLeft,
  ArrowUpRight,
  MapPin,
  Calendar,
  ImageIcon,
  Lock,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { VOLT } from "@/lib/elektryk-gallery";
import { elektrykPublicListRealizacje } from "@/lib/elektryk-admin.functions";

type PublicReal = {
  id: string;
  slug: string;
  title: string;
  description: string | null;
  location: string | null;
  date_label: string | null;
  images: { id: string; url: string; alt: string | null }[];
};

export const Route = createFileRoute("/demo/elektryk_/realizacje")({
  head: () => ({
    meta: [
      { title: "Realizacje — ELEKTROPROFI" },
      {
        name: "description",
        content:
          "Portfolio ELEKTROPROFI: rozdzielnice, smart home, monitoring, pomiary, instalacje przemysłowe.",
      },
      { property: "og:title", content: "Realizacje — ELEKTROPROFI" },
      { property: "og:description", content: "Pełne portfolio prac instalatorskich." },
    ],
  }),
  component: RealizacjeFeed,
  errorComponent: ({ error }) => (
    <div className="grid min-h-screen place-items-center bg-[#070708] px-6 text-center text-white/70">
      <div>
        <p className="text-sm">Nie udało się załadować realizacji.</p>
        <p className="mt-1 text-xs text-white/40">{error.message}</p>
      </div>
    </div>
  ),
  notFoundComponent: () => (
    <div className="grid min-h-screen place-items-center bg-[#070708] text-white/70">
      Nie znaleziono.
    </div>
  ),
});

function RealizacjeFeed() {
  const listFn = useServerFn(elektrykPublicListRealizacje);
  const { data, isLoading } = useQuery({
    queryKey: ["elektryk", "realizacje", "public"],
    queryFn: () => listFn(),
    staleTime: 1000 * 60 * 5,
  });

  const items = (data ?? []) as PublicReal[];
  const [openId, setOpenId] = useState<string | null>(null);
  const open = openId ? items.find((i) => i.id === openId) ?? null : null;

  return (
    <div className="min-h-screen overflow-x-clip bg-[#070708] font-sans text-[#F5F5F7] antialiased selection:bg-[#00F0FF] selection:text-black">
      <header className="sticky top-0 z-40 border-b border-white/[0.06] bg-[#070708]/95">
        <div className="mx-auto flex max-w-3xl items-center justify-between gap-4 px-5 py-4">
          <Link
            to="/demo/elektryk"
            className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.02] px-3 py-1.5 text-[11px] uppercase tracking-[0.18em] text-white/70 transition hover:border-[#00F0FF]/40 hover:text-white"
          >
            <ArrowLeft size={12} /> ELEKTROPROFI
          </Link>
          <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">Realizacje</div>
          <Link
            to="/demo/elektryk_/admin"
            className="inline-flex items-center gap-1 rounded-full border border-white/10 px-2.5 py-1.5 text-[11px] uppercase tracking-[0.18em] text-white/50 hover:text-white"
            title="Panel admina"
          >
            <Lock size={10} />
          </Link>
        </div>
      </header>

      <section className="relative px-5 pt-14 pb-8 md:pt-20">
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-[360px]"
          style={{ background: `radial-gradient(360px 360px at 50% 10%, ${VOLT}18, transparent 70%)` }}
        />
        <div className="relative mx-auto max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[0.03] px-3 py-1 text-[10px] uppercase tracking-[0.28em] text-white/65">
            <span className="h-1 w-1 rounded-full" style={{ backgroundColor: VOLT, boxShadow: `0 0 6px ${VOLT}` }} />
            Portfolio
          </div>
          <h1 className="mt-5 text-balance text-4xl font-light leading-[0.95] tracking-tighter text-white sm:text-5xl">
            Wszystkie realizacje<span style={{ color: VOLT }}>.</span>
          </h1>
          <p className="mt-4 max-w-xl text-sm leading-relaxed text-white/70 md:text-base">
            Przewijaj jak feed. Kliknij dowolną realizację, by zobaczyć szczegóły, opis i pełną galerię.
          </p>
        </div>
      </section>

      <section className="px-4 pb-24 md:pb-32">
        <div className="mx-auto max-w-xl space-y-6 md:max-w-2xl">
          {isLoading &&
            Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="aspect-[4/5] animate-pulse rounded-3xl border border-white/[0.05] bg-white/[0.02]"
              />
            ))}
          {!isLoading && items.length === 0 && (
            <div className="rounded-3xl border border-dashed border-white/10 p-12 text-center text-sm text-white/55">
              Brak opublikowanych realizacji. Dodaj pierwszą w{" "}
              <Link
                to="/demo/elektryk_/admin"
                className="underline decoration-dotted underline-offset-4 hover:text-white"
              >
                panelu administratora
              </Link>
              .
            </div>
          )}
          {!isLoading &&
            items.map((it, i) => (
              <FeedCard key={it.id} item={it} eager={i < 2} onOpen={() => setOpenId(it.id)} />
            ))}
        </div>

        <div className="mt-20 flex flex-col items-center gap-4 border-t border-white/[0.06] pt-12 text-center">
          <div className="text-[10px] uppercase tracking-[0.28em] text-white/65">
            Chcesz podobną realizację?
          </div>
          <Link
            to="/demo/elektryk"
            hash="kontakt"
            className="group inline-flex items-center gap-2 rounded-full px-7 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-black transition"
            style={{ backgroundColor: VOLT, boxShadow: `0 0 32px ${VOLT}55` }}
          >
            Skontaktuj się
            <ArrowUpRight size={14} className="transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>
      </section>

      {open && <RealizationLightbox item={open} onClose={() => setOpenId(null)} />}
    </div>
  );
}

function FeedCard({
  item,
  eager,
  onOpen,
}: {
  item: PublicReal;
  eager: boolean;
  onOpen: () => void;
}) {
  const cover = item.images[0];
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group block w-full overflow-hidden rounded-3xl border border-white/[0.06] bg-[#0B0B0E] text-left transition hover:border-[#00F0FF]/30 focus:outline-none focus-visible:border-[#00F0FF]/60"
      aria-label={`Otwórz szczegóły: ${item.title}`}
    >
      <div className="relative aspect-square overflow-hidden bg-black md:aspect-[4/5]">
        {cover ? (
          <img
            src={cover.url}
            alt={cover.alt ?? item.title}
            loading={eager ? "eager" : "lazy"}
            decoding="async"
            fetchPriority={eager ? "high" : "low"}
            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
          />
        ) : (
          <div className="grid h-full w-full place-items-center text-white/30">
            <ImageIcon size={36} />
          </div>
        )}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-1/2 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
        {item.images.length > 1 && (
          <span className="absolute right-3 top-3 rounded-full bg-black/65 px-2.5 py-1 text-[10px] uppercase tracking-[0.16em] text-white/85 backdrop-blur-sm">
            +{item.images.length - 1}
          </span>
        )}
        <div className="absolute inset-x-5 bottom-5">
          <h2 className="text-xl font-medium leading-tight tracking-tight text-white md:text-2xl">
            {item.title}
          </h2>
          <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] uppercase tracking-[0.16em] text-white/70">
            {item.location && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin size={11} /> {item.location}
              </span>
            )}
            {item.date_label && (
              <span className="inline-flex items-center gap-1.5">
                <Calendar size={11} /> {item.date_label}
              </span>
            )}
          </div>
        </div>
        <span
          className="absolute right-3 bottom-3 grid h-9 w-9 place-items-center rounded-full bg-white/90 text-black opacity-0 transition group-hover:opacity-100"
          style={{ boxShadow: `0 0 22px ${VOLT}` }}
        >
          <ArrowUpRight size={15} />
        </span>
      </div>
    </button>
  );
}

function RealizationLightbox({ item, onClose }: { item: PublicReal; onClose: () => void }) {
  const images = item.images;
  const [idx, setIdx] = useState(0);
  const [dir, setDir] = useState<1 | -1>(1);
  const [entered, setEntered] = useState(false);
  const [closing, setClosing] = useState(false);
  const total = images.length;

  const prev = useCallback(() => {
    if (total < 2) return;
    setDir(-1);
    setIdx((i) => (i - 1 + total) % total);
  }, [total]);
  const next = useCallback(() => {
    if (total < 2) return;
    setDir(1);
    setIdx((i) => (i + 1) % total);
  }, [total]);

  const requestClose = useCallback(() => {
    if (closing) return;
    setClosing(true);
    window.setTimeout(onClose, 240);
  }, [closing, onClose]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") requestClose();
      else if (e.key === "ArrowLeft") prev();
      else if (e.key === "ArrowRight") next();
    }
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      cancelAnimationFrame(raf);
    };
  }, [requestClose, prev, next]);

  // Swipe handling
  const touchStartX = useRef<number | null>(null);
  const touchDeltaX = useRef(0);
  function onTouchStart(e: React.TouchEvent) {
    touchStartX.current = e.touches[0]?.clientX ?? null;
    touchDeltaX.current = 0;
  }
  function onTouchMove(e: React.TouchEvent) {
    if (touchStartX.current == null) return;
    touchDeltaX.current = (e.touches[0]?.clientX ?? 0) - touchStartX.current;
  }
  function onTouchEnd() {
    const dx = touchDeltaX.current;
    touchStartX.current = null;
    touchDeltaX.current = 0;
    if (Math.abs(dx) < 50) return;
    if (dx > 0) prev();
    else next();
  }

  const current = images[idx];
  const visible = entered && !closing;

  return (
    <div
      className="fixed inset-0 z-[60] flex items-stretch justify-center sm:items-center sm:p-6"
      style={{
        backgroundColor: visible ? "rgba(0,0,0,0.85)" : "rgba(0,0,0,0)",
        backdropFilter: visible ? "blur(8px)" : "blur(0px)",
        WebkitBackdropFilter: visible ? "blur(8px)" : "blur(0px)",
        transition: "background-color 240ms ease, backdrop-filter 240ms ease, -webkit-backdrop-filter 240ms ease",
      }}
      role="dialog"
      aria-modal="true"
      aria-label={item.title}
      onClick={requestClose}
    >
      <div
        className="relative flex h-full w-full max-w-4xl flex-col overflow-hidden bg-[#0A0A0C] text-white sm:h-auto sm:max-h-[92vh] sm:rounded-3xl sm:border sm:border-white/[0.08]"
        style={{
          opacity: visible ? 1 : 0,
          transform: visible ? "translateY(0) scale(1)" : "translateY(14px) scale(0.97)",
          transition: "opacity 260ms ease, transform 260ms cubic-bezier(0.22, 1, 0.36, 1)",
          willChange: "transform, opacity",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between gap-3 border-b border-white/[0.06] px-5 py-3.5">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-[0.22em] text-white/55">Realizacja</div>
            <h2 className="truncate text-base font-medium tracking-tight text-white sm:text-lg">
              {item.title}
            </h2>
          </div>
          <button
            type="button"
            onClick={requestClose}
            className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-white/15 text-white/80 transition hover:border-white/40 hover:text-white"
            aria-label="Zamknij"
          >
            <X size={16} />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {/* Image stage */}
          <div
            className="relative aspect-square w-full select-none bg-black sm:aspect-[4/5]"
            onTouchStart={onTouchStart}
            onTouchMove={onTouchMove}
            onTouchEnd={onTouchEnd}
          >
            {current ? (
              <img
                key={current.id}
                src={current.url}
                alt={current.alt ?? item.title}
                decoding="async"
                className="h-full w-full object-cover"
                style={{
                  animation: `${dir === 1 ? "lb-slide-in-right" : "lb-slide-in-left"} 360ms cubic-bezier(0.22, 1, 0.36, 1) both`,
                  willChange: "transform, opacity",
                }}
                draggable={false}
              />
            ) : (
              <div className="grid h-full w-full place-items-center text-white/30">
                <ImageIcon size={42} />
              </div>
            )}

            {total > 1 && (
              <>
                <button
                  type="button"
                  onClick={prev}
                  aria-label="Poprzednie zdjęcie"
                  className="absolute left-3 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-black/55 text-white backdrop-blur-sm transition hover:bg-black/80"
                >
                  <ChevronLeft size={18} />
                </button>
                <button
                  type="button"
                  onClick={next}
                  aria-label="Następne zdjęcie"
                  className="absolute right-3 top-1/2 grid h-10 w-10 -translate-y-1/2 place-items-center rounded-full bg-black/55 text-white backdrop-blur-sm transition hover:bg-black/80"
                >
                  <ChevronRight size={18} />
                </button>
                <div className="absolute bottom-3 left-1/2 flex -translate-x-1/2 gap-1.5">
                  {images.map((im, i) => (
                    <button
                      key={im.id}
                      type="button"
                      onClick={() => { setDir(i >= idx ? 1 : -1); setIdx(i); }}
                      aria-label={`Zdjęcie ${i + 1}`}
                      className="h-1.5 rounded-full transition-all"
                      style={{
                        width: i === idx ? 22 : 8,
                        backgroundColor: i === idx ? VOLT : "rgba(255,255,255,0.45)",
                      }}
                    />
                  ))}
                </div>
                <span className="absolute right-3 top-3 rounded-full bg-black/65 px-2.5 py-1 text-[10px] uppercase tracking-[0.16em] text-white/85">
                  {idx + 1} / {total}
                </span>
              </>
            )}
          </div>

          {/* Thumbnails */}
          {total > 1 && (
            <div className="border-b border-white/[0.05] px-4 py-3">
              <div className="flex gap-2 overflow-x-auto pb-1">
                {images.map((im, i) => (
                  <button
                    key={`t-${im.id}`}
                    type="button"
                    onClick={() => { setDir(i >= idx ? 1 : -1); setIdx(i); }}
                    aria-label={`Pokaż zdjęcie ${i + 1}`}
                    className="relative shrink-0 overflow-hidden rounded-lg border transition"
                    style={{
                      borderColor: i === idx ? VOLT : "rgba(255,255,255,0.08)",
                      boxShadow: i === idx ? `0 0 0 1px ${VOLT}` : undefined,
                    }}
                  >
                    <img
                      src={im.url}
                      alt=""
                      loading="lazy"
                      decoding="async"
                      className="h-14 w-20 object-cover opacity-90"
                    />
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Meta + description */}
          <div className="px-5 py-5 sm:px-7 sm:py-6">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] uppercase tracking-[0.18em] text-white/65">
              {item.location && (
                <span className="inline-flex items-center gap-1.5">
                  <MapPin size={12} /> {item.location}
                </span>
              )}
              {item.date_label && (
                <span className="inline-flex items-center gap-1.5">
                  <Calendar size={12} /> {item.date_label}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5">
                <ImageIcon size={12} /> {total} {total === 1 ? "zdjęcie" : "zdjęć"}
              </span>
            </div>

            {item.description ? (
              <p className="mt-5 whitespace-pre-line text-sm leading-relaxed text-white/80 sm:text-[15px]">
                {item.description}
              </p>
            ) : (
              <p className="mt-5 text-sm italic text-white/40">Brak opisu dla tej realizacji.</p>
            )}

            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/demo/elektryk"
                hash="kontakt"
              onClick={requestClose}
                className="inline-flex items-center gap-2 rounded-full px-5 py-3 text-[11px] font-semibold uppercase tracking-[0.2em] text-black transition"
                style={{ backgroundColor: VOLT, boxShadow: `0 0 24px ${VOLT}55` }}
              >
                Wycena podobnej
                <ArrowUpRight size={13} />
              </Link>
              <button
                type="button"
              onClick={requestClose}
                className="inline-flex items-center gap-2 rounded-full border border-white/15 px-5 py-3 text-[11px] uppercase tracking-[0.2em] text-white/75 hover:border-white/40 hover:text-white"
              >
                Zamknij
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}